import {
  keepPreviousData,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { scorecardKeys } from '@/hooks/query-keys'
import {
  createScorecardKpi,
  createScorecardActual,
  deleteScorecardKpi,
  getCompanyScorecard,
  getPortalScorecard,
  getScorecardMetricOptions,
  updateScorecardKpi,
  type CreateScorecardKpiInput,
  type CreateScorecardActualInput,
  type ScorecardMetricOptionsResponseDto,
  type ScorecardPeriodType,
  type UpdateScorecardKpiInput,
} from '@/services/scorecard.service'

export function useCompanyScorecard(companyId: string) {
  return useQuery({
    queryKey: scorecardKeys.company(companyId),
    queryFn: () => getCompanyScorecard(companyId),
    enabled: Boolean(companyId),
    staleTime: 2 * 60 * 1000,
  })
}

export function usePortalScorecard(enabled = true) {
  return useQuery({
    queryKey: scorecardKeys.portal(),
    queryFn: getPortalScorecard,
    enabled,
    staleTime: 2 * 60 * 1000,
  })
}

export function useScorecardMetricOptions(
  companyId: string,
  periodType: ScorecardPeriodType,
  enabled = true,
  includeMetricLabelId?: string
) {
  return useQuery({
    queryKey: scorecardKeys.metricOptions(
      companyId,
      periodType,
      includeMetricLabelId
    ),
    queryFn: () =>
      getScorecardMetricOptions(companyId, periodType, includeMetricLabelId),
    enabled: enabled && Boolean(companyId) && Boolean(periodType),
    placeholderData: keepPreviousData,
    staleTime: 2 * 60 * 1000,
  })
}

export function useScorecardMetricOptionsForPeriods(
  companyId: string,
  periodTypes: ScorecardPeriodType[],
  enabled = true,
  includeMetricLabelId?: string
) {
  const queries = useQueries({
    queries: periodTypes.map((periodType) => ({
      queryKey: scorecardKeys.metricOptions(
        companyId,
        periodType,
        includeMetricLabelId
      ),
      queryFn: () =>
        getScorecardMetricOptions(companyId, periodType, includeMetricLabelId),
      enabled: enabled && Boolean(companyId),
      placeholderData: keepPreviousData,
      staleTime: 2 * 60 * 1000,
    })),
  })

  const data = queries.reduce<ScorecardMetricOptionsResponseDto[]>(
    (responses, query) => {
      if (query.data) responses.push(query.data)
      return responses
    },
    []
  )

  return {
    data,
    isLoading: queries.some((query) => query.isLoading),
    isFetching: queries.some((query) => query.isFetching),
    isError: queries.some((query) => query.isError),
  }
}

export function useCreateScorecardKpi(companyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: CreateScorecardKpiInput) =>
      createScorecardKpi(companyId, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: scorecardKeys.company(companyId),
        }),
        queryClient.invalidateQueries({
          queryKey: scorecardKeys.metricOptionsForCompany(companyId),
        }),
        queryClient.invalidateQueries({ queryKey: scorecardKeys.portal() }),
      ])
    },
  })
}

export function useUpdateScorecardKpi(companyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      kpiId,
      data,
    }: {
      kpiId: string
      data: UpdateScorecardKpiInput
    }) => updateScorecardKpi(companyId, kpiId, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: scorecardKeys.company(companyId),
        }),
        queryClient.invalidateQueries({ queryKey: scorecardKeys.portal() }),
      ])
    },
  })
}

export function useDeleteScorecardKpi(companyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (kpiId: string) => deleteScorecardKpi(companyId, kpiId),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: scorecardKeys.company(companyId),
        }),
        queryClient.invalidateQueries({ queryKey: scorecardKeys.portal() }),
        queryClient.invalidateQueries({
          queryKey: scorecardKeys.metricOptionsForCompany(companyId),
        }),
      ])
    },
  })
}

export function useCreateScorecardActual(companyId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      kpiId,
      data,
    }: {
      kpiId: string
      data: CreateScorecardActualInput
    }) => createScorecardActual(companyId, kpiId, data),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: scorecardKeys.company(companyId),
        }),
        queryClient.invalidateQueries({ queryKey: scorecardKeys.portal() }),
        queryClient.invalidateQueries({
          queryKey: ['company-metric-series', companyId],
        }),
        queryClient.invalidateQueries({
          queryKey: ['portal', 'metrics-series'],
        }),
      ])
    },
  })
}
